import { faPlus, faShare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createSequence } from '@olivierpascal/helpers';
import { Button, Stack } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import {
  AppUserListItem,
  AppUserListItemSkeleton,
} from '~/components/molecules/AppUserListItem';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { AppOrganizationUserListItem } from '~/components/molecules/OrganizationUserListItem';
import { Table } from '~/components/molecules/Table';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useAppUsersQuery } from '~/lib/api/modules/Apps/hooks/useAppUsersQuery';
import { useOrganizationUsersQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationUsersQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(2, 1fr) minmax(140px, min-content)';
const DEFAULT_SKELETON_COUNT = 3;

export type IAppUserListProps = IContainerProps & {
  appId: string;
};

const styles = stylex.create({
  test: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridColumn: '1/-1',
  },
});

export const AppUserList: React.FC<IAppUserListProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const appQuery = useAppQuery({ appId });
  const appUsersQuery = useAppUsersQuery({
    appId,
  });
  const organizationId = appQuery.data?.organization?._id;
  const organizationUsersQuery = useOrganizationUsersQuery(
    organizationId ? { organizationId } : undefined,
  );

  const isLoading =
    appQuery.isLoading ||
    appUsersQuery.isLoading ||
    organizationUsersQuery.isLoading;
  const error =
    appQuery.error ?? appUsersQuery.error ?? organizationUsersQuery.error;

  const appOrganizationUsers = organizationUsersQuery.data?.items.filter(
    ({ roles }) => roles?.includes('owner') || roles?.includes('admin'),
  );
  const appUsers = appUsersQuery.data?.items.filter(
    ({ _id }) => !appOrganizationUsers?.some((user) => user._id === _id),
  );
  const isEmpty =
    !appUsersQuery.isLoading &&
    !appOrganizationUsers?.length &&
    !appUsers?.length;

  return (
    <ContentBlock sx={sx}>
      {error ? (
        <ContentBlock.Body first>
          <ErrorMessage error={error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('AppUserList.errors.empty.title')} />
      ) : (
        <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
          <Table.Header>{t('common.name')}</Table.Header>
          <Table.Header>{t('common.role')}</Table.Header>
          <Table.Header />
          {isLoading ? (
            createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
              <AppUserListItemSkeleton key={index} />
            ))
          ) : (
            <>
              {appUsers?.map((user) => (
                <AppUserListItem
                  key={user._id}
                  appId={appId}
                  user={user}
                  organizationId={organizationId}
                />
              ))}

              <Table.Section
                action={
                  organizationId && (
                    <Button
                      href={`/organizations/${organizationId}/users`}
                      variant="outlined"
                    >
                      {t('AppUserList.manageOrganization')}
                    </Button>
                  )
                }
              >
                {t('AppUserList.inheritedUsers')}
              </Table.Section>

              {organizationId ? (
                appOrganizationUsers?.map((user) => (
                  <AppOrganizationUserListItem
                    key={user._id}
                    organizationId={organizationId}
                    user={user}
                  />
                ))
              ) : (
                <ContentBlock.Empty
                  title={t('AppUserList.errors.outsideOrganization.title')}
                  subtitle={t(
                    'AppUserList.errors.outsideOrganization.subtitle',
                  )}
                  sx={styles.test}
                >
                  <Stack horizontal gap={2} justify="center">
                    <Button
                      icon={<FontAwesomeIcon icon={faPlus} />}
                      href="/organizations/create"
                      variant="text"
                    >
                      {t(
                        'AppUserList.errors.outsideOrganization.createOrganization',
                      )}
                    </Button>
                    <Button
                      icon={<FontAwesomeIcon icon={faShare} />}
                      href={`/apps/${appId}/settings/advanced`}
                      variant="text"
                    >
                      {t(
                        'AppUserList.errors.outsideOrganization.transferApplication',
                      )}
                    </Button>
                  </Stack>
                </ContentBlock.Empty>
              )}
            </>
          )}
        </Table>
      )}
    </ContentBlock>
  );
};
